if (!customElements.get('gift-counter-310')) {
    customElements.define('gift-counter-310', class GiftCounter310 extends HTMLElement {
        constructor() {
            super();
            // Timer duration in milliseconds, parsed from data-attribute
            this.TIMER_DURATION = parseInt(this.dataset.timerDuration) * 1000;
            // The variant ID of the free gift (must be passed via Liquid)
            this.GIFT_VARIANT_ID = parseInt(this.dataset.giftVariantId);

            // DOM elements and intervals
            this.container = this.querySelector('.gift-counter');
            this.counterDisplay = this.querySelector('.gift-counter__counter__value');
            this.intervalId = null;
            this.cartObserver = null;
            this.cartUnsubscriber = null; // Unsubscribe function from pub/sub

            // Bind 'this' context for consistent event handling (best practice)
            this.handleDelegatedClick = this.handleDelegatedClick.bind(this);
        }

        connectedCallback() {
            // Check current cart state on load
            const cartItems = document.querySelectorAll('.cart-item, .cart__item');

            if (cartItems.length > 0) {
                // If items exist, run the main logic immediately
                this.checkAndInitTimer();
                // Add click delegation for the checkout button (using pre-bound function)
                document.addEventListener('click', this.handleDelegatedClick);
            } else {
                // If the cart is empty, subscribe to cart updates to initiate the timer later
                const callback = (e) => {
                    this.checkAndInitTimer();
                    // Add click delegation only after successful cart addition (first time)
                    document.addEventListener('click', this.handleDelegatedClick);

                    // Unsubscribe after the first successful check/run
                    if (this.cartUnsubscriber) {
                        this.cartUnsubscriber();
                        this.cartUnsubscriber = null; 
                    }
                }

                // Subscribe to cart update events. Passed directly since 'callback' is defined here.
                this.cartUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, callback);
            }
        }

        /**
         * Main decision-making logic for starting/resuming/blocking the timer.
         */
        checkAndInitTimer() {
            // 1. FETCH LOCAL STORAGE DATA
            let dailyRun = localStorage.getItem('GC_DailyRun');
            let endTime = localStorage.getItem('GC_EndTime');
            let giftAdded = localStorage.getItem('GC_IsAdded');

            // 2. MIDNIGHT RESET LOGIC (New Day Check)
            if (dailyRun) {
                const lastRunDate = new Date(parseInt(dailyRun));
                const today = new Date();

                // If the date string is different (new calendar day), reset all states.
                if (lastRunDate.toDateString() !== today.toDateString()) {
                    localStorage.removeItem('GC_DailyRun');
                    localStorage.removeItem('GC_EndTime');
                    localStorage.removeItem('GC_IsAdded');

                    // Reset local variables to reflect the 'clean' state below
                    dailyRun = null;
                    endTime = null;
                    giftAdded = null;

                    // Remove any lingering gift item from the cart from the previous day
                    this.removeGiftFromCart();
                    
                    // Set flag indicating the counter was reset today, requiring the drawer check
                    localStorage.setItem('GC_IsResetted', 'true');
                }
            }

            // 3. BLOCKING CONDITIONS (Valid for the same calendar day)

            // A. Gift already successfully added to cart (GC_IsAdded = true)
            if (giftAdded === 'true') {
                this.hideBanner();
                // Enforce removal if the user returns to cart, enforcing strict "in-time" purchase
                this.removeGiftFromCart();
                return;
            }

            // B. Timer is currently running/active (page refresh) - RESUME
            if (endTime && parseInt(endTime) > Date.now()) {
                this.startTimer(parseInt(endTime));
                return;
            }

            // C. Timer has finished (GC_EndTime has expired) - BLOCK
            if (endTime && parseInt(endTime) <= Date.now()) {
                this.hideBanner();
                this.removeGiftFromCart(); // Ensure no gift is in the cart after expiration
                return;
            }

            // 4. INITIALIZATION (Triggered only if no blocking conditions met)
            if (this.isInitialGiftInCart()) {
                const isResettedDailyCounter = localStorage.getItem('GC_IsResetted');
                
                // If the counter was reset today (GC_IsResetted is set), wait for the cart drawer to open
                if (isResettedDailyCounter) {
                    const cartDrawerElement = document.querySelector('cart-drawer');

                    if (!cartDrawerElement) {
                        console.warn("Could not find the cart drawer element.");
                        // Fallback: If no drawer, start timer immediately if items are present
                        this.startTimer(); 
                        return;
                    }

                    // MutationObserver configuration
                    const config = { attributes: true };

                    // Observer callback logic
                    const observerCallback = (mutationsList, observer) => {
                        for (const mutation of mutationsList) {
                            if (mutation.type === 'attributes') {
                                
                                // 🛑 Adjust selector and class/attribute check for your theme 🛑
                                // Note: Using document.querySelector ensures the element is found dynamically
                                const isActive = document.querySelector('.wt-cart__drawer').classList.contains('wt-cart__drawer--open');

                                if (isActive) {
                                    this.startTimer();
                                    
                                    // Clean up flag and observer
                                    localStorage.removeItem('GC_IsResetted');
                                    observer.disconnect(); // Use the provided observer reference
                                    this.cartObserver = null;
                                    
                                    // Stop loop
                                    return;
                                }
                            }
                        }
                    };

                    this.cartObserver = new MutationObserver(observerCallback);
                    this.cartObserver.observe(cartDrawerElement, config);
                } else {
                    // If no daily reset occurred (first run of the day, or resumed session)
                    this.startTimer();
                }
            } else {
                // If cart is empty, hide the banner and wait for cart update events.
                this.hideBanner();
            }
        }

        /**
         * Starts or resumes the countdown timer.
         * @param {number} existingEndTime - Optional existing end time for continuation.
         */
        startTimer(existingEndTime = null) {
            const endTime = existingEndTime || Date.now() + this.TIMER_DURATION;

            // Save state only if this is a fresh start
            if (!existingEndTime) {
                localStorage.setItem('GC_DailyRun', Date.now().toString());
                localStorage.setItem('GC_EndTime', endTime.toString());
            }

            this.showBanner();
            this.updateCounter(endTime);

            // Clear previous interval to prevent duplication
            if (this.intervalId) clearInterval(this.intervalId);

            this.intervalId = setInterval(() => {
                this.updateCounter(endTime);
            }, 1000);
        }

        /**
         * Updates the counter display every second.
         * @param {number} endTime - The exact time (timestamp) when the timer expires.
         */
        updateCounter(endTime) {
            const remainingTime = endTime - Date.now();

            if (remainingTime <= 0) {
                clearInterval(this.intervalId);
                this.counterDisplay.textContent = '00:00';
                // Keep GC_EndTime to maintain the daily block
                this.onTimerEnd();
                return;
            }

            const totalSeconds = Math.floor(remainingTime / 1000);
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;

            this.counterDisplay.textContent =
                `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        }

        /**
         * Logic executed when the countdown reaches zero.
         */
        onTimerEnd() {
            this.hideBanner();
            this.removeGiftFromCart();
        }

        /**
         * Checks if the cart contains any items necessary to activate the counter.
         * @returns {boolean} True if the cart has items.
         */
        isInitialGiftInCart() {
            const cartItems = document.querySelectorAll('.cart-item, .cart__item'); 
            return cartItems.length > 0;
        }

        /**
         * Helper function to check if the free gift is present in the cart DOM 
         * or if the flag GC_IsAdded is set. 
         * @returns {boolean} True if the gift is likely in the cart or recently added.
         */
        isGiftInCart() {
            const giftItem = document.querySelector(`[data-variant-id="${this.GIFT_VARIANT_ID}"], a[href*="variant=${this.GIFT_VARIANT_ID}"]`) || localStorage.getItem('GC_IsAdded') === 'true';
            return !!giftItem;
        }

        showBanner() {
            this.style.display = 'block';
            this.container.style.display = 'flex';
        }

        hideBanner() {
            this.style.display = 'none';
            this.container.style.display = 'none';

            const checkoutButton = document.querySelector('cart-drawer button[name="checkout"], a[href*="/checkout"]');
            const regularText = checkoutButton.querySelector('.checkout-text--regular');
            const freeGiftText = checkoutButton.querySelector('.checkout-text--free-gift');
            if (regularText) {
                regularText.classList.remove('hidden');
            }
            if (freeGiftText) {
                freeGiftText.classList.add('hidden');
            }
        }

        /**
         * Handles click events delegated from the document, focusing on the Checkout button.
         */
        handleDelegatedClick(e) {
            const checkoutButton = e.target.closest('button[name="checkout"], a[href*="/checkout"]');

            if (checkoutButton) {
                // Prevent double-handling during rapid clicks
                if (checkoutButton.dataset.handled !== 'true') {
                    checkoutButton.dataset.handled = 'true';
                    this.handleCheckout(e);
                    setTimeout(() => { delete checkoutButton.dataset.handled; }, 500);
                }
            }
        }

        /**
         * Intercepts the checkout process to add or remove the gift based on timer status.
         * @param {Event} e - The click event.
         */
        async handleCheckout(e) {
            const endTime = localStorage.getItem('GC_EndTime');
            const giftAdded = localStorage.getItem('GC_IsAdded');
            const isTimerActive = endTime && parseInt(endTime) > Date.now();

            // SCENARIO 1: Time is active, gift not yet added -> ADD GIFT
            if (isTimerActive && giftAdded !== 'true') {
                e.preventDefault();
                e.target.disabled = true;
                e.target.textContent = 'Adding gift...';

                const success = await this.addFreeGift();

                if (success) {
                    localStorage.setItem('GC_IsAdded', 'true');
                    window.location.href = '/checkout';
                } else {
                    window.location.href = '/checkout';
                }
                return;
            }

            // SCENARIO 2: Time expired, but gift is still in cart -> REMOVE GIFT
            else if (!isTimerActive && this.isGiftInCart()) {
                e.preventDefault();
                const removeSuccess = await this.removeGiftFromCart();

                if (removeSuccess) {
                    return;
                }
                window.location.href = '/checkout';
            }

            // SCENARIO 3: Standard checkout
        }

        /**
         * Sends an AJAX request to /cart/add.js to add the free gift variant.
         * @returns {Promise<boolean>} Success status.
         */
        async addFreeGift() {
            const formData = {
                items: [{
                    id: this.GIFT_VARIANT_ID,
                    quantity: 1,
                    properties: { '_counter_free_gift': 'true' }
                }]
            };
            try {
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData)
                });
                return response.ok;
            } catch (error) {
                console.error('Network error during gift addition:', error);
                return false;
            }
        }

        /**
         * Sends an AJAX request to /cart/update.js to remove the gift by setting quantity to 0.
         * @returns {Promise<boolean>} Success status.
         */
        async removeGiftFromCart() {
            if (!this.isGiftInCart()) {
                return false;
            }

            if (!this.GIFT_VARIANT_ID) return false;

            const formData = {
                updates: {
                    [this.GIFT_VARIANT_ID]: 0
                }
            };

            try {
                const response = await fetch('/cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData)
                });

                if (response.ok) {
                    localStorage.removeItem('GC_IsAdded');
                    await this.refreshCart();
                    return true;
                }
                return false;
            } catch (error) {
                console.error('Network error during gift removal:', error);
                return false;
            }
        }

        /**
         * Triggers a cart refresh using the theme's custom element method or reloads the page.
         */
        async refreshCart() {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && cartDrawer.refreshCartDrawer) {
                await cartDrawer.refreshCartDrawer();
            } else {
                window.location.reload();
            }
        }
    });
}